// i18n
import { translateInComponent, supportLocales } from 'infras/i18n';
import translations from 'presentations/views/components/pages/AccountSettingProfilePage/i18n.json';

import { useState, useEffect, SyntheticEvent, useContext } from 'react';
import MainDocument from 'presentations/views/components/MainDocument';
import AccountSettingProfilePage from 'presentations/views/components/pages/AccountSettingProfilePage';
import AccountSettingForm from 'presentations/views/components/organisms/AccountSettingForm';
import { destroy, get, patch } from 'infras/rest_client';
import { apiPath, accountSettingProfilePath } from 'presentations/routes/path';
import { ErrorList } from 'domains/models';
import UserSetting from 'domains/models/user_setting';
import { getLocales } from 'domains/models/user_setting/locale';
import { UserProfileItem, UserUpdateBody } from 'presentations/api/schema/user';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import { authorize as clientAuthorize } from 'presentations/views/helpers/auth/session';
import { serviceVariantColors } from 'presentations/views/styles/color';
import { useRequest } from 'presentations/views/hooks/request';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { useRouter } from 'next/router';
import { setEvent } from 'presentations/views/helpers/analytics';

export default function Page() {
  const t = translateInComponent(translations);
  const router = useRouter();

  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = dashboardState.viewer!;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorList>({});
  const [locale, setLocale] = useState<string | null>(null);

  const [color, setColor] = useState<string | null>();
  const [slug, setSlug] = useState<string | null>(null);
  const [profile, setProfile] = useState<string | null>(null);
  const [editColor, setEditColor] = useState<string | null>(null);

  const colors = serviceVariantColors;
  const [originalColor, setOriginalColor] = useState<string>('#000000');

  const locales = getLocales().filter((l) => supportLocales.includes(l.value));

  const [timeZone, setTimeZone] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem = userProfileRequest.data;

  useEffect(() => {
    if (!userProfile) return;
    setLocale(userProfile.locale);
    setTimeZone(userProfile.timeZone);
    setColor(userProfile.color);
    setProfile(userProfile.profile);
    setSlug(userProfile.slug);
    const defaultOriginalColor = colors.includes(userProfile.color)
      ? '#000000'
      : userProfile.color;
    setOriginalColor(defaultOriginalColor);
  }, [userProfile]);

  useEffect(() => {
    if (!viewer) return;
    setProfileImage(viewer.profileImage || null);
  }, [viewer]);

  const onChange = (
    e: SyntheticEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.currentTarget;
    if (!locale || !timeZone) return;
    const userSetting = new UserSetting({
      name: '',
      userId: viewer.uid,
      locale,
      timeZone,
    });
    switch (name) {
      case 'locale':
        setLocale(value);
        userSetting.locale = value;
        break;
      case 'color':
        setEditColor(value);
        break;
      case 'slug':
        setSlug(value);
        break;
      case 'profile':
        setProfile(value);
        break;
      default:
        break;
    }
    setErrors({});
    userSetting.isValid();
    setErrors(userSetting.errors);
  };
  const onChangeOriginalColor = (color: string) => {
    setOriginalColor(color);
  };

  const onSubmit = async (name: string) => {
    switch (name) {
      case 'locale': {
        if (!locale) return;
        update({ locale });
        setEvent('user update locale');
        const { pathname, asPath, query } = router;
        router.push({ pathname, query }, asPath, { locale });
        break;
      }
      case 'color': {
        if (editColor) {
          updateTeamUser({ color: editColor });
          setColor(editColor);
          setEditColor(null);
        }
        setEvent('user update color');
        break;
      }
      case 'timeZone': {
        if (!timeZone) return;
        update({ timeZone });
        break;
      }
      case 'slug': {
        updateTeamUser({ slug });
        setEvent('user update slug');
        break;
      }
      case 'profile': {
        updateTeamUser({ profile });
        setEvent('user update profile');
        break;
      }
      case 'profileImage': {
        setIsLoading(true);
        if (profileImageFile) {
          const data = new FormData();
          data.set('profileImageFile', profileImageFile);
          try {
            const result = await patch(
              apiPath('/v1/user/profile_image/update'),
              {
                data,
              },
            );
            setExpanded(null);
            const newViewer = await clientAuthorize();
            if (newViewer) {
              setProfileImage(newViewer.profileImage);
            }
          } catch (error) {
            console.debug(error);
          } finally {
            setIsLoading(false);
          }
        }
        setEvent('user update profile image');
        break;
      }
      default:
        break;
    }
  };

  const update = async (data: UserUpdateBody) => {
    setIsLoading(true);
    const result = await patch(apiPath('/v1/user/update'), { data });
    if (!result.isSuccess) {
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }
    setExpanded(null);
    setIsLoading(false);
  };

  const updateTeamUser = async (data: { [key: string]: any }) => {
    if (!viewer.team) return;
    setIsLoading(true);
    const result = await patch(
      apiPath(`/v1/teams/${viewer.team.uid}/users/${viewer.uid}/update`),
      { data },
    );
    if (!result.isSuccess) {
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }
    setExpanded(null);
    setIsLoading(false);
  };

  const onExpand = (e: SyntheticEvent<HTMLLinkElement>, panel: string) => {
    e.preventDefault();
    if (panel === expanded) {
      if (expanded === 'color') {
        setEditColor(null);
      }
      setExpanded(null);
    } else {
      setExpanded(panel);
    }
  };

  const onChangeProfileImage = async (file: any) => {
    setProfileImageFile(file);
  };

  const onDeleteProfileImage = async () => {
    setProfileImageFile(null);
    if (profileImageFile) return;
    setIsLoading(true);
    const result = await destroy(apiPath(`/v1/user/profile_image/delete`));
    setIsLoading(false);
    if (!result.isSuccess) {
      setErrors(result.errors);
      return;
    }
    setProfileImage(null);
    setExpanded(null);
  };
  const onSelectTimeZone = async (name: string) => {
    setTimeZone(name);
  };

  return (
    <MainDocument title={t('title')} description={t('description')}>
      <AccountSettingProfilePage
        viewer={viewer}
        isLoading={userProfileRequest.isLoading}
        currentUrl={accountSettingProfilePath()}
        form={
          <AccountSettingForm
            isLoading={isLoading}
            onChange={onChange}
            onChangeOriginalColor={onChangeOriginalColor}
            onSubmit={onSubmit}
            onExpand={onExpand}
            onChangeProfileImage={onChangeProfileImage}
            onDeleteProfileImage={onDeleteProfileImage}
            expanded={expanded}
            errors={errors}
            locale={locale}
            locales={locales}
            color={editColor || color!}
            originalColor={originalColor}
            colors={colors}
            timeZone={timeZone}
            slug={slug!}
            profile={profile}
            teamSlug={viewer.team?.slug!}
            profileImage={profileImage}
            profileImageFile={profileImageFile}
            onSelectTimeZone={onSelectTimeZone}
          />
        }
      />
    </MainDocument>
  );
}
